import type { FormAnalyticsAggregateResponse } from '@gofranz/formshive-common';
import { Card, Group, SimpleGrid, Skeleton, Text, ThemeIcon } from '@mantine/core';
import { IconClock, IconDeviceLaptop, IconWorld } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

export interface PillStatsProps {
  aggregate: FormAnalyticsAggregateResponse | null;
  loading: boolean;
}

/**
 * Format a duration in seconds as a short human-readable string:
 * "45s", "1m 45s", "2h 5m".
 */
export function formatAvgTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return '—';
  }
  const total = Math.round(seconds);
  if (total < 60) {
    return `${total}s`;
  }
  if (total < 3600) {
    const m = Math.floor(total / 60);
    const s = total % 60;
    return s ? `${m}m ${s}s` : `${m}m`;
  }
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  return m ? `${h}h ${m}m` : `${h}h`;
}

interface PillProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  loading: boolean;
}

function Pill({ icon, label, value, loading }: PillProps) {
  return (
    <Card withBorder padding="lg" radius="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Text size="sm" c="dimmed" fw={500}>
          {label}
        </Text>
        <ThemeIcon size={32} radius="xl" variant="light" color="gray">
          {icon}
        </ThemeIcon>
      </Group>
      {loading ? (
        <Skeleton h={28} w={100} mt="md" />
      ) : (
        <Text fz={24} fw={700} lh={1.1} mt="md" truncate>
          {value}
        </Text>
      )}
    </Card>
  );
}

export function PillStats({ aggregate, loading }: PillStatsProps) {
  const { t } = useTranslation();

  const avgSeconds = aggregate?.average_time_to_submit_message?.average_time_seconds ?? 0;
  const topCountry = aggregate?.top_countries?.[0]?.country ?? '—';
  const topBrowser = aggregate?.top_browsers?.[0]?.browser ?? '—';

  return (
    <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
      <Pill
        icon={<IconClock size={20} />}
        label={t('messages.avgTime')}
        value={formatAvgTime(avgSeconds)}
        loading={loading}
      />
      <Pill
        icon={<IconWorld size={20} />}
        label={t('messages.topCountry')}
        value={topCountry}
        loading={loading}
      />
      <Pill
        icon={<IconDeviceLaptop size={20} />}
        label={t('messages.topBrowser')}
        value={topBrowser}
        loading={loading}
      />
    </SimpleGrid>
  );
}
